/**
 * KuCoin REST API Client
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-all-symbols
 */

import type {
  KucoinBulletPublicResponse,
  KucoinCandlesRequest,
  KucoinCandlesResponse,
  KucoinOrderbookRequest,
  KucoinOrderbookResponse,
  KucoinStats24hRequest,
  KucoinStats24hResponse,
  KucoinSymbolResponse,
  KucoinSymbolsListResponse,
  KucoinTradesRequest,
  KucoinTradesResponse,
} from './types';

import { get, post } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.kucoin.com/api';

export const kucoinRestEndpoints = {
  symbolsList: `${BASE_URL}/v2/symbols`,
  symbol: (pair: string) => `${BASE_URL}/v2/symbols/${pair}`,
  orderbook: (level: 20 | 100 = 100) => `${BASE_URL}/v1/market/orderbook/level2_${level}`,
  candles: `${BASE_URL}/v1/market/candles`,
  trades: `${BASE_URL}/v1/market/histories`,
  bulletPublic: `${BASE_URL}/v1/bullet-public`,
  stats24h: `${BASE_URL}/v1/market/stats`,
} as const;

// ========================================
// Symbol Helpers
// ========================================

/**
 * Formats base and quote currencies into KuCoin symbol format.
 * KuCoin uses uppercase dash format: BTC-USDT
 *
 * @example
 * formatKucoinSymbol('BTC', 'USDT') // 'BTC-USDT'
 */
export const formatKucoinSymbol = (base: string, quote: string): string => {
  return `${base.toUpperCase()}-${quote.toUpperCase()}`;
};

/**
 * Parses a KuCoin symbol into base and quote currencies.
 *
 * @example
 * parseKucoinSymbol('BTC-USDT') // { base: 'BTC', quote: 'USDT' }
 */
export const parseKucoinSymbol = (symbol: string): { base: string; quote: string } => {
  const [base, quote] = symbol.toUpperCase().split('-');
  return { base: base || '', quote: quote || '' };
};

// ========================================
// REST API Functions
// ========================================

/**
 * Retrieves list of trading symbols and their metadata (including ST flag).
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-all-symbols
 */
export const getKucoinSymbolsList = async (
  options?: RestRequestOptions,
): Promise<KucoinSymbolsListResponse> => {
  const response = await get<KucoinSymbolsListResponse>({
    url: kucoinRestEndpoints.symbolsList,
    options,
  });

  return response.data;
};

/**
 * Retrieves metadata for a single trading symbol (precision, increments).
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-symbol
 */
export const getKucoinSymbol = async (
  pair: string,
  options?: RestRequestOptions,
): Promise<KucoinSymbolResponse> => {
  const response = await get<KucoinSymbolResponse>({
    url: kucoinRestEndpoints.symbol(pair),
    options,
  });

  return response.data;
};

/**
 * Grabs level2 orderbook snapshot.
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-part-orderbook
 */
export const getKucoinOrderbook = async (
  params: KucoinOrderbookRequest,
  options?: RestRequestOptions,
): Promise<KucoinOrderbookResponse> => {
  const { level = 100, ...rest } = params;
  const response = await get<KucoinOrderbookResponse, KucoinOrderbookRequest>({
    url: kucoinRestEndpoints.orderbook(level),
    params: rest,
    options,
  });

  return response.data;
};

/**
 * Fetches candlestick data (returned in reverse chronological order).
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-klines
 */
export const getKucoinCandles = async (
  params: KucoinCandlesRequest,
  options?: RestRequestOptions,
): Promise<KucoinCandlesResponse> => {
  const response = await get<KucoinCandlesResponse, KucoinCandlesRequest>({
    url: kucoinRestEndpoints.candles,
    params,
    options,
  });

  return response.data;
};

/**
 * Returns the most recent trade fills for the symbol.
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-trade-history
 */
export const getKucoinTrades = async (
  params: KucoinTradesRequest,
  options?: RestRequestOptions,
): Promise<KucoinTradesResponse> => {
  const response = await get<KucoinTradesResponse, KucoinTradesRequest>({
    url: kucoinRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};

/**
 * Initializes a public bullet session required for WebSocket connections.
 * @see https://www.kucoin.com/docs-new/websocket-api/base-info/get-public-token-spot-margin
 */
export const createKucoinBulletPublicSession = async (
  options?: RestRequestOptions,
): Promise<KucoinBulletPublicResponse> => {
  const response = await post<KucoinBulletPublicResponse>({
    url: kucoinRestEndpoints.bulletPublic,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour statistics for a symbol.
 * @see https://www.kucoin.com/docs-new/rest/spot-trading/market-data/get-24hr-stats
 */
export const getKucoinStats24h = async (
  params: KucoinStats24hRequest,
  options?: RestRequestOptions,
): Promise<KucoinStats24hResponse> => {
  const response = await get<KucoinStats24hResponse, KucoinStats24hRequest>({
    url: kucoinRestEndpoints.stats24h,
    params,
    options,
  });

  return response.data;
};
